// Reglas puras del paso Precio (sin DOM) para discount-form y range-calendar; `node --test`. Límites
// desde Discount vía la vista (`mins`, `maxDays`); de la UI: máximo 90, paso de 1 y 2 meses adelante.
// Doc: _docs/features/products/partner_product_form.md

export const KINDS = ["permanent", "flash", "all_day"]
export const KIND_LABEL = { permanent: "Permanente", flash: "Relámpago", all_day: "Del día" }
export const MAX_PERCENT = 90
export const MONTHS_AHEAD = 2

// Mismo orden y valores que Discount::DAY_NAMES (sin tilde, lunes primero)
export const DAY_NAMES = ["Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado", "Domingo"]
export const DAY_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
const WEEKDAY_ABBR = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"] // índice de Date#getDay
const MONTH_ABBR = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]
const MONTH_NAME = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]

// ---------- Tipo y porcentaje (mins y maxDays vienen de la vista) ----------

export function kindDescription(kind, { mins, maxDays }) {
  if (kind === "flash") return `En los horarios y días que elijas. Desde ${minPercent(kind, mins)}%, con contador en Ofertas.`
  if (kind === "all_day") return `Todo el día, de 1 a ${maxDays} días seguidos. Desde ${minPercent(kind, mins)}%, con contador en Ofertas.`
  return "Se repite cada semana, todo el día, en los días que elijas."
}

export function minPercent(kind, mins) {
  return Number(mins?.[kind] ?? mins?.permanent) || 0
}

export function percentHelp(kind, mins) {
  const min = minPercent(kind, mins)
  return kind === "permanent" ? `Entre ${min}% y ${MAX_PERCENT}%` : `Mínimo ${min}%`
}

export function clampPercent(percent, kind, mins) {
  const min = minPercent(kind, mins)
  const value = Number(percent)
  if (!Number.isFinite(value) || value < min) return min
  return Math.min(MAX_PERCENT, value)
}

export function finalPrice(price, percent) {
  return Math.round((Number(price) || 0) * (1 - (Number(percent) || 0) / 100))
}

// ---------- Días y horarios ----------

// "todos los días" · tramos de 3 o más seguidos "Mar a Sáb" · sueltos "Lun, Mié y Vie"
export function daysText(days) {
  const on = DAY_NAMES.map((_, index) => Boolean(days[index]))
  if (on.every(Boolean)) return "todos los días"
  const parts = []
  let index = 0
  while (index < on.length) {
    if (!on[index]) { index += 1; continue }
    let last = index
    while (last + 1 < on.length && on[last + 1]) last += 1
    if (last - index >= 2) parts.push(`${DAY_SHORT[index]} a ${DAY_SHORT[last]}`)
    else for (let day = index; day <= last; day += 1) parts.push(DAY_SHORT[day])
    index = last + 1
  }
  if (parts.length === 0) return "ningún día"
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}`
}

export function windowsShort(windows) {
  return windows.map(window => `${window.start}–${window.end}`).join(", ")
}

export function windowsLong(windows) {
  return windows.map(window => `de ${window.start} a ${window.end}`).join(" y ")
}

// Fin menor que inicio = termina al día siguiente (válido; el servidor lo evalúa así)
export function crossesMidnight(window) {
  return Boolean(window.start && window.end) && window.end < window.start
}

// Solo fin igual a inicio (o un campo vacío) es error: el cruce de medianoche se conserva
export function windowInvalid(window) {
  return !window.start || !window.end || window.start === window.end
}

// ---------- Descuento completo ----------
// d = { active, kind, percent, days: bool[7], windows: [{ start, end }], startDate, endDate }

export function discountError(d) {
  if (!d.active) return ""
  if (d.kind !== "all_day" && !d.days.some(Boolean)) return "Elige al menos un día para el descuento."
  if (d.kind === "flash" && d.windows.some(windowInvalid)) return "Revisa los horarios: el fin debe ser después del inicio."
  if (d.kind === "all_day" && (!d.startDate || !d.endDate)) return "Elige las fechas de la oferta del día"
  return ""
}

// "Relámpago · 08:00–11:30, 16:00–19:10 · Mar a Sáb" (sin el porcentaje: lo usa la vista del cliente)
export function discountLine(d) {
  if (d.kind === "permanent") return `Permanente · ${daysText(d.days)}`
  if (d.kind === "flash") return `Relámpago · ${windowsShort(d.windows)} · ${daysText(d.days)}`
  return `Del día · ${dateRangeShort(d.startDate, d.endDate)}`
}

// Resumen de la cabecera de la tarjeta: "−25% · Relámpago · …" o "Sin descuento"
export function discountSummary(d) {
  return d.active ? `−${d.percent}% · ${discountLine(d)}` : "Sin descuento"
}

// Frase del recuadro Precio final
export function discountSentence(d) {
  if (d.kind === "permanent") return `Se aplica todo el día, ${daysText(d.days)}.`
  if (d.kind === "flash") return `Se aplica ${daysText(d.days)}, ${windowsLong(d.windows)}. Aparece con contador en Ofertas.`
  return `Se aplica todo el día, ${dateRangeLong(d.startDate, d.endDate)}. Aparece con contador en Ofertas.`
}

// ---------- Fechas (ISO "YYYY-MM-DD", en hora local) ----------

export function parseISO(iso) {
  const [year, month, day] = String(iso).split("-").map(Number)
  return new Date(year, month - 1, day)
}

export function toISO(date) {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function addDays(iso, days) {
  const date = parseISO(iso)
  date.setDate(date.getDate() + days)
  return toISO(date)
}

export function daysBetween(fromISO, toISOString) {
  return Math.round((parseISO(toISOString) - parseISO(fromISO)) / 86400000)
}

export function rangeDays(start, end) {
  if (!start || !end) return 0
  return daysBetween(start, end) + 1
}

// "jue 8"
export function dateShort(iso) {
  const date = parseISO(iso)
  return `${WEEKDAY_ABBR[date.getDay()]} ${date.getDate()}`
}

// "jue 8 – sáb 10 oct" · "jue 8 oct" · "mié 30 sep – vie 2 oct"
export function dateRangeShort(start, end) {
  if (!start) return ""
  const a = parseISO(start)
  const b = parseISO(end || start)
  if (toISO(a) === toISO(b)) return `${dateShort(start)} ${MONTH_ABBR[a.getMonth()]}`
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
  return `${dateShort(start)}${sameMonth ? "" : ` ${MONTH_ABBR[a.getMonth()]}`} – ${dateShort(end)} ${MONTH_ABBR[b.getMonth()]}`
}

// "del jue 8 al sáb 10 de oct" · "el jue 8 de oct"
export function dateRangeLong(start, end) {
  if (!start) return ""
  const a = parseISO(start)
  const b = parseISO(end || start)
  if (toISO(a) === toISO(b)) return `el ${dateShort(start)} de ${MONTH_ABBR[a.getMonth()]}`
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
  return `del ${dateShort(start)}${sameMonth ? "" : ` de ${MONTH_ABBR[a.getMonth()]}`} al ${dateShort(end)} de ${MONTH_ABBR[b.getMonth()]}`
}

export function monthTitle(year, month) {
  return `${MONTH_NAME[month]} ${year}`
}

// ---------- Calendario de rango ----------

// Celdas del mes (lunes a domingo); pasados, otros meses y más allá de inicio + (maxDays − 1) deshabilitados
export function monthCells({ year, month, today, start, end, picking, maxDays }) {
  const first = new Date(year, month, 1)
  const lead = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const rows = Math.ceil((lead + daysInMonth) / 7)
  const limit = picking && start ? addDays(start, maxDays - 1) : null
  const multi = Boolean(start && end && start !== end)
  return Array.from({ length: rows * 7 }, (_, index) => {
    const date = new Date(year, month, 1 - lead + index)
    const iso = toISO(date)
    const inMonth = date.getMonth() === month
    const past = iso < today
    const disabled = !inMonth || past || (limit !== null && iso > limit)
    const isStart = inMonth && iso === start
    const isEnd = inMonth && iso === (end || start)
    const inRange = inMonth && Boolean(start) && iso >= start && iso <= (end || start)
    let band = "none"
    if (multi && inRange) band = isStart ? "start" : isEnd ? "end" : "mid"
    return { iso, day: date.getDate(), inMonth, past, disabled, isStart, isEnd, inRange, isToday: iso === today, band }
  })
}

// Primer toque fija el inicio; segundo dentro del rango fija el fin (mismo día = 1 día); otro reinicia
export function pickDate(range, iso, maxDays) {
  if (range.picking && range.start && iso >= range.start && iso <= addDays(range.start, maxDays - 1)) {
    return { start: range.start, end: iso, picking: false }
  }
  return { start: iso, end: iso, picking: true }
}

export function calendarHint(range, maxDays) {
  if (!range.start) return { text: "", tone: "done" }
  if (range.picking) {
    return {
      text: `Toca el último día (hasta el ${dateShort(addDays(range.start, maxDays - 1))}). Para un solo día, toca de nuevo el ${dateShort(range.start)}.`,
      tone: "picking"
    }
  }
  const days = rangeDays(range.start, range.end || range.start)
  return {
    text: `${dateRangeShort(range.start, range.end)} · ${days} ${days === 1 ? "día" : "días"}. Toca otra fecha para empezar de nuevo.`,
    tone: "done"
  }
}

// Meses de distancia entre el mes de `today` y el de `iso`, acotado a [0, monthsAhead]
export function monthOffset(today, iso, monthsAhead = MONTHS_AHEAD) {
  if (!iso) return 0
  const a = parseISO(today)
  const b = parseISO(iso)
  const offset = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
  return Math.max(0, Math.min(monthsAhead, offset))
}
